import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import IntentWizard from '../components/onboarding/IntentWizard';
import OnboardingHeader from '../components/OnboardingHeader';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

const OnboardingQuestions = () => {
  const { user, apiCall } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  // iter-230j — Inline error state with retry. Previously a failure here
  // only toasted and silently let the page sit — combined with the parent
  // navigate-on-mount in VendorTaxRegistration, this produced the
  // "flicker for 2-3 min then silently land on /dashboard" symptom.
  const [error, setError] = useState(null);
  const [lastFormData, setLastFormData] = useState(null);

  const submitProfile = async (formData) => {
    setSaving(true);
    setError(null);
    setLastFormData(formData);
    try {
      const res = await apiCall('/api/vendors/onboarding-profile', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      localStorage.removeItem('onboarding_redirect');
      localStorage.setItem('onboarding_redirect', res.next_step || '/vendor/tax-registration');

      toast({
        title: 'Profile saved',
        description: 'Moving to the next step...',
      });

      navigate(res.next_step || '/vendor/tax-registration', { replace: true });
    } catch (err) {
      // iter-230j — DO NOT navigate on failure. Stay on this page and
      // show an inline error card with a Retry button. Auto-navigating
      // on error is what previously made the loop end at /dashboard with
      // no banner — completely opaque to the user.
      const isTimeout = err?.code === 'TIMEOUT';
      setError({
        type: isTimeout ? 'timeout' : 'api',
        message: err?.message || 'Failed to save profile. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = (formData) => submitProfile(formData);
  const handleRetry = () => {
    if (lastFormData) submitProfile(lastFormData);
  };

  if (saving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center" data-testid="onboarding-saving-spinner">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-3 text-gray-600">Saving your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div
          className="w-full max-w-md bg-white rounded-xl shadow-sm border border-red-100 p-6 text-center"
          data-testid="onboarding-error-card"
        >
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {error.type === 'timeout' ? 'Connection timed out' : "We couldn't save your profile"}
          </h2>
          <p className="text-sm text-gray-600 mb-5">{error.message}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="onboarding-retry-btn"
            >
              <RefreshCw className="h-4 w-4" /> Retry
            </button>
            <button
              type="button"
              onClick={() => setError(null)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="onboarding-edit-btn"
            >
              Edit answers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="onboarding-questions-page">
      <OnboardingHeader subtitle="Profile Creation" />
      <div className="flex items-center justify-center p-4 py-10">
        <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm border p-6 md:p-10">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Tell us about yourself</h1>
          <p className="text-gray-500 mt-1">Help us personalise your experience</p>
        </div>
        <IntentWizard
          onComplete={handleComplete}
          initialData={user?.onboarding_profile || {}}
          isInternational={user?.is_international || false}
        />
        </div>
      </div>
    </div>
  );
};

export default OnboardingQuestions;
